import { ArrowLeft, Plug } from "lucide-react";
import { type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useParams } from "react-router";

import { CallsChart } from "@/components/calls-chart";
import { FilterBar } from "@/components/filter-bar";
import { IssueStatusChip } from "@/components/issue-status-chip";
import { LoadErrorCard, LoadingCard } from "@/components/load-state";
import { MeasureSwitch } from "@/components/segmented-control";
import { TimeRangeFilter } from "@/components/time-range-filter";
import { ToolIcon } from "@/components/tool-icon";
import { RetiredChip } from "@/components/tool-name";
import { Card, CardContent } from "@/components/ui/card";
import { type McpIssueDetail, useAdminMcpIssue, useMe } from "@/lib/api";
import type { ChartMeasure } from "@/lib/calls-chart";
import { formatDateTime, formatNumber, formatShare } from "@/lib/format";
import {
  FAILURES_PATH,
  issueCategoryLabel,
  issueChartDays,
  issueKindLabel,
  issueToolLabel,
} from "@/lib/issues";
import { type Period, usePeriod } from "@/lib/period";
import { isAdminRole, isDeveloper } from "@/lib/roles";
import { useTableSort } from "@/lib/table-sort";
import { timeRangeQuery, useTimeRange } from "@/lib/time-range";
import { isRetiredTool, useToolTitles } from "@/lib/tool-titles";
import { cn } from "@/lib/utils";

import { CALL_SORT_COLUMNS, CALL_SORT_INITIAL, CallLog } from "../calls/call-log";
import { ChartSection, EmptyChart, PeriodBar, SectionHeading, StaleNote } from "../parts";
import { failuresBackSearch, loadState } from "../utils";
import { IssueActions } from "./issue-actions";
import { IssueHistory } from "./issue-history";

/**
 * One issue's page (`/analytics/fouten/<key>`): what failed (the kind, the
 * tool, the status, a developer's status changes and every admin role's
 * "Notitie toevoegen"), the facts (first and last seen, the failures in the
 * period and over the 12 months kept, the people, and the tools for a failure
 * of the connector as a whole), its failures per day, its failed calls (the
 * call log with its time range, sort and pages), and its history: the status
 * changes and the notes, each with its author. The period in the tinted bar drives the facts, the chart and the
 * calls; the calls' time range offers the same periods (choosing one moves
 * the whole page) or a custom range for the calls alone, all in the URL
 * (`?days=`, `?from=&to=`). A key no kept call failed under says so, with the
 * way back.
 */
export function IssuePage() {
  const { t } = useTranslation();
  const { key = "" } = useParams();
  const location = useLocation();
  const [days, setDays] = usePeriod();
  const { data: me } = useMe();
  const admin = isAdminRole(me?.role);
  const developer = isDeveloper(me?.role);
  const issueQuery = useAdminMcpIssue(key, days);
  const issue = issueQuery.data;
  const state = loadState(issueQuery);
  const notFound = (issueQuery.error as { status?: number } | null)?.status === 404;
  const retry = () => void issueQuery.refetch();

  return (
    <div className="space-y-8">
      <Link
        // Back to the list as it was left (its filters and sort), over this page's period.
        to={{ pathname: FAILURES_PATH, search: failuresBackSearch(location.state, days) }}
        className="text-accent tap-target -mt-2 inline-flex items-center gap-1 text-sm font-medium hover:underline pointer-coarse:relative"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        {t("issues.page.back")}
      </Link>
      {issueQuery.isLoading ? (
        <LoadingCard />
      ) : notFound ? (
        <Card>
          <CardContent className="text-muted py-8 text-center text-sm">
            {t("issues.page.notFound")}
          </CardContent>
        </Card>
      ) : state === "failed" || !issue ? (
        <LoadErrorCard message={t("issues.page.loadError")} onRetry={retry} />
      ) : (
        <>
          <IssueHeader issue={issue} admin={admin} developer={developer} days={days} />
          <PeriodBar days={days} onChange={setDays} />
          {state === "stale" && <StaleNote onRetry={retry} />}
          <div
            className={cn(
              "space-y-8 transition-opacity",
              issueQuery.isPlaceholderData && "opacity-60",
            )}
          >
            <IssueFacts issue={issue} />
            <FailuresPerDay issue={issue} />
          </div>
          <section aria-labelledby="issue-calls">
            <SectionHeading id="issue-calls">{t("issues.page.calls")}</SectionHeading>
            {/* A fresh sort when the page's period changes. */}
            <IssueCalls key={days} issueKey={issue.key} />
          </section>
          {admin && (
            <section aria-labelledby="issue-history">
              <SectionHeading id="issue-history">{t("issues.history.title")}</SectionHeading>
              <IssueHistory entries={issue.history} />
            </section>
          )}
        </>
      )}
    </div>
  );
}

interface IssueHeaderProps {
  issue: McpIssueDetail;
  /** Whether the reader has an admin role, who adds notes. */
  admin: boolean;
  /** Whether the reader is a developer, who also changes the status. */
  developer: boolean;
  days: Period;
}

/** The kind as the heading, the tool and the key under it, the status, and the reader's actions. */
function IssueHeader({ issue, admin, developer, days }: IssueHeaderProps) {
  const { t } = useTranslation();
  const titles = useToolTitles();
  return (
    <header className="space-y-4">
      <div className="flex items-start gap-3">
        {issue.tool === null ? (
          <Plug
            aria-hidden="true"
            strokeWidth={1.75}
            className="text-primary mt-1 size-5 shrink-0"
          />
        ) : (
          <ToolIcon name={issue.tool} className="text-primary mt-1 size-5 shrink-0" />
        )}
        <div className="min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <h2 className="font-heading text-primary text-xl font-bold">
              {issueKindLabel(issue.error_type)}
            </h2>
            <IssueStatusChip status={issue.status} />
          </div>
          <p className="text-foreground flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-sm">
            <span className="font-medium">{issueToolLabel(issue.tool, titles)}</span>
            <code className="text-muted font-mono text-xs wrap-anywhere">{issue.key}</code>
            {issue.tool !== null && isRetiredTool(titles, issue.tool) && <RetiredChip />}
          </p>
          <p className="text-muted text-xs">
            {issueCategoryLabel(issue.category)}
            {issue.status_changed_at &&
              // A come-back's status date is when it was resolved, before it failed again.
              `, ${t(
                issue.status === "regressed"
                  ? "issues.page.regressedSince"
                  : "issues.page.statusSince",
                { time: formatDateTime(issue.status_changed_at) },
              )}`}
          </p>
        </div>
      </div>
      {admin && (
        <IssueActions
          issueKey={issue.key}
          status={issue.status}
          days={days}
          developer={developer}
        />
      )}
    </header>
  );
}

/**
 * The issue's facts: when it was first and last seen, how often and for whom
 * it failed, the period's failures with their share of the calls of its tool
 * (of every call for a failure of the connector as a whole).
 */
function IssueFacts({ issue }: { issue: McpIssueDetail }) {
  const { t } = useTranslation();
  const calls = issue.per_day.reduce((sum, day) => sum + day.calls, 0);
  const share =
    calls > 0
      ? t(issue.tool === null ? "analytics.shareOfCalls" : "issues.page.facts.shareOfToolCalls", {
          share: formatShare(issue.count / calls),
        })
      : undefined;
  // A count reads large; a moment a size smaller, so it fits a phone's half width.
  const facts: {
    key: string;
    term: string;
    value: ReactNode;
    note?: string;
    moment?: boolean;
  }[] = [
    {
      key: "period",
      term: t("issues.page.facts.inPeriod"),
      value: formatNumber(issue.count),
      note: share,
    },
    { key: "total", term: t("issues.page.facts.total"), value: formatNumber(issue.total) },
    { key: "users", term: t("issues.page.facts.users"), value: formatNumber(issue.users) },
    ...(issue.tool === null
      ? [{ key: "tools", term: t("issues.page.facts.tools"), value: formatNumber(issue.tools) }]
      : []),
    {
      key: "last",
      term: t("issues.page.facts.lastSeen"),
      value: formatDateTime(issue.last_seen),
      moment: true,
    },
    {
      key: "first",
      term: t("issues.page.facts.firstSeen"),
      value: formatDateTime(issue.first_seen),
      moment: true,
    },
  ];
  return (
    <dl className="grid grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-4">
      {facts.map((fact) => (
        <div key={fact.key} className="border-border bg-card rounded-lg border px-3 py-2.5 sm:px-4">
          <dt className="text-muted text-xs hyphens-auto">{fact.term}</dt>
          <dd
            className={cn(
              "text-primary font-bold tabular-nums",
              fact.moment ? "text-sm sm:text-base" : "text-lg",
            )}
          >
            {fact.value}
          </dd>
          {fact.note && (
            <dd className="text-muted text-xs hyphens-auto tabular-nums">{fact.note}</dd>
          )}
        </div>
      ))}
    </dl>
  );
}

/** The issue's failures per day, as a count or a share of that day's calls of its tool. */
function FailuresPerDay({ issue }: { issue: McpIssueDetail }) {
  const { t } = useTranslation();
  const [measure, setMeasure] = useState<ChartMeasure>("count");
  return (
    <ChartSection
      title={t("issues.perDay")}
      aside={issue.count > 0 && <MeasureSwitch value={measure} onChange={setMeasure} />}
    >
      {issue.count === 0 ? (
        <EmptyChart>{t("issues.page.noFailures", { count: issue.days })}</EmptyChart>
      ) : (
        <CallsChart data={issueChartDays(issue.per_day)} series="errors" measure={measure} />
      )}
    </ChartSection>
  );
}

/** The issue page's time range in its URL: the page's period, or a custom range for the calls beside it. */
const ISSUE_RANGE_URL = { keepPeriod: true } as const;

/**
 * The issue's failed calls: the call log, filtered on the issue, with its
 * time range, its sort and its pages. The time range is the page's period
 * (`?days=`, the same as the tinted bar's, which a choice here moves too), or
 * a custom range for the calls alone (`?from=&to=`), so both controls always
 * say the same period and a reload or a shared link keeps the range.
 */
function IssueCalls({ issueKey }: { issueKey: string }) {
  const { t } = useTranslation();
  const [range, setRange] = useTimeRange(ISSUE_RANGE_URL);
  const [days] = usePeriod();
  const sort = useTableSort({ columns: CALL_SORT_COLUMNS, initial: CALL_SORT_INITIAL });
  const filtered = range.kind === "custom";
  const reset = () => setRange({ kind: "period", days });
  return (
    <div className="space-y-3">
      <FilterBar columns={["wideRow"]} filtered={filtered} onReset={reset}>
        <TimeRangeFilter value={range} onChange={setRange} />
      </FilterBar>
      <CallLog
        query={{ ...timeRangeQuery(range), issueKey }}
        sort={sort}
        label={t("issues.page.calls")}
        onReset={filtered ? reset : undefined}
      />
    </div>
  );
}
